import { Point } from './types';

const validPoint = (point: Point) => Number.isFinite(point.x) && Number.isFinite(point.y);

// Store the point on the curve at t=0.5, so the dragged handle stays under the pointer.
export const curveControlPoint = (start: Point, end: Point, midpoint: Point): Point => ({
  x: 2 * midpoint.x - (start.x + end.x) / 2,
  y: 2 * midpoint.y - (start.y + end.y) / 2,
});

export const quadraticPoint = (start: Point, control: Point, end: Point, t: number): Point => ({
  x: (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x,
  y: (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y,
});

export const arrowGeometry = (start: Point, end: Point, width: number) => {
  if (!validPoint(start) || !validPoint(end)) return null;
  const dx = end.x - start.x, dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length) return null;
  const ux = dx / length, uy = dy / length;
  const depth = Math.min(length, (12 + Math.max(0, width) * 2) * Math.cos(0.45));
  const halfWidth = depth * Math.tan(0.45);
  const shaftEnd = { x: end.x - ux * depth, y: end.y - uy * depth };
  return {
    tip: end,
    left: { x: shaftEnd.x - uy * halfWidth, y: shaftEnd.y + ux * halfWidth },
    right: { x: shaftEnd.x + uy * halfWidth, y: shaftEnd.y - ux * halfWidth },
    shaftEnd,
    shaftPath: `M${start.x},${start.y} L${shaftEnd.x},${shaftEnd.y}`,
    shaftVisible: length > depth,
  };
};

export const curvedArrowGeometry = (start: Point, control: Point, end: Point, width: number) => {
  if (![start, control, end].every(validPoint)) return null;
  const depth = (12 + Math.max(0, width) * 2) * Math.cos(0.45);
  const distance = (t: number) => {
    const point = quadraticPoint(start, control, end, t);
    return Math.hypot(end.x - point.x, end.y - point.y);
  };
  // Find the last head-length of the curve, including curves that turn back on themselves.
  let low = 1;
  while (low > 0 && distance(low) < depth) low = Math.max(0, low - 1 / 32);
  let high = Math.min(1, low + 1 / 32);
  if (distance(low) >= depth) {
    for (let step = 0; step < 24; step++) {
      const middle = (low + high) / 2;
      if (distance(middle) >= depth) low = middle;
      else high = middle;
    }
  }
  const t = low;
  const shaftEnd = quadraticPoint(start, control, end, t);
  const head = arrowGeometry(shaftEnd, end, width);
  if (!head) return null;
  const shaftControl = { x: start.x + (control.x - start.x) * t, y: start.y + (control.y - start.y) * t };
  return { ...head, shaftVisible: t > 0,
    shaftPath: `M${start.x},${start.y} Q${shaftControl.x},${shaftControl.y} ${shaftEnd.x},${shaftEnd.y}` };
};

const signedAngle = (start: Point, vertex: Point, end: Point) => {
  if (![start, vertex, end].every(validPoint)) return null;
  const ax = start.x - vertex.x, ay = start.y - vertex.y;
  const bx = end.x - vertex.x, by = end.y - vertex.y;
  if (!Math.hypot(ax, ay) || !Math.hypot(bx, by)) return null;
  return Math.atan2(ax * by - ay * bx, ax * bx + ay * by);
};

export const angleSweep = (start: Point, vertex: Point, end: Point, direction = 0, previousAngle?: number) => {
  const angle = signedAngle(start, vertex, end);
  if (angle == null) return null;
  const crossedStart = previousAngle != null && (angle === 0 || previousAngle === 0 ||
    (Math.sign(angle) !== Math.sign(previousAngle) && Math.abs(angle - previousAngle) < Math.PI));
  const nextDirection = crossedStart ? Math.sign(angle) : direction || Math.sign(angle);
  return { angle, direction: nextDirection, reflexAngle: angle * nextDirection < 0 };
};

export const angleLabelGeometry = (start: Point, vertex: Point, end: Point, width: number, fontSize = 24, reflexAngle = false) => {
  const angle = signedAngle(start, vertex, end);
  if (angle == null) return null;
  const ax = start.x - vertex.x, ay = start.y - vertex.y;
  const minorDegrees = Math.abs(angle) * 180 / Math.PI;
  const degrees = reflexAngle ? 360 - minorDegrees : minorDegrees;
  const outside = reflexAngle || degrees < 60 - 1e-8;
  const bisector = Math.atan2(ay, ax) + angle / 2;
  // A circle enclosing the text keeps every orientation clear of both stroked arms.
  const textRadius = Math.hypot(`${Math.round(degrees)}°`.length * fontSize * 0.34, fontSize * 0.65);
  const clearance = textRadius + Math.max(0, width) / 2 + 8;
  const distance = outside ? -clearance : clearance / Math.sin(Math.abs(angle) / 2);
  return { position: { x: vertex.x + Math.cos(bisector) * distance, y: vertex.y + Math.sin(bisector) * distance }, degrees, outside };
};

export const cornerResizeScale = (anchor: Point, corner: Point, pointer: Point) => {
  const dx = corner.x - anchor.x, dy = corner.y - anchor.y;
  return ((pointer.x - anchor.x) * dx + (pointer.y - anchor.y) * dy) / (dx * dx + dy * dy || 1);
};
